import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useQueryClient } from "@tanstack/react-query";
import { notificationSound } from "@/lib/notification-sound";
import { safeRemoveChannel } from "@/lib/supabase-channel";

export interface WaiterCallNotification {
  id: string;
  table_number: string;
  created_at: string;
}

export function useWaiterCallNotifications() {
  const { activeRestaurant } = useRestaurantContext();
  const queryClient = useQueryClient();
  const [newCalls, setNewCalls] = useState<WaiterCallNotification[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!activeRestaurant?.id) return;

    // Clean up previous channel if exists
    if (channelRef.current) {
      safeRemoveChannel(channelRef.current);
      channelRef.current = null;
    }

    // Subscribe to new waiter calls
    channelRef.current = supabase
      .channel(`waiter-calls-${activeRestaurant.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "waiter_calls",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        (payload) => {
          const newCall = payload.new as {
            id: string;
            table_number: string;
            created_at: string;
            status: string;
          };

          if (newCall.status === "pending") {
            setNewCalls((prev) => {
              // Avoid duplicates
              if (prev.some((c) => c.id === newCall.id)) return prev;
              return [
                {
                  id: newCall.id,
                  table_number: newCall.table_number,
                  created_at: newCall.created_at,
                },
                ...prev,
              ];
            });

            // Play notification sound
            notificationSound.playNewOrder();

            // Invalidate queries
            queryClient.invalidateQueries({ queryKey: ["waiter-calls"] });
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "waiter_calls",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        (payload) => {
          const updatedCall = payload.new as {
            id: string;
            status: string;
          };

          // Remove from notifications if acknowledged or resolved
          if (updatedCall.status !== "pending") {
            setNewCalls((prev) => prev.filter((c) => c.id !== updatedCall.id));
            queryClient.invalidateQueries({ queryKey: ["waiter-calls"] });
          }
        }
      )
      .subscribe((status) => {
        console.log("[WaiterCallNotifications] Subscription status:", status);
      });

    return () => {
      safeRemoveChannel(channelRef.current);
      channelRef.current = null;
    };
  }, [activeRestaurant?.id, queryClient]);

  const dismissCall = (callId: string) => {
    setNewCalls((prev) => prev.filter((c) => c.id !== callId));
  };

  const clearAllCalls = () => {
    setNewCalls([]);
  };

  return {
    newCalls,
    hasNewCalls: newCalls.length > 0,
    dismissCall,
    clearAllCalls,
  };
}
